import { useEffect, useRef } from 'react'
import { useControls, useTransformEffect } from 'react-zoom-pan-pinch'

import { useShelfRegistry } from '@components/storeMap/floor/ShelfRegistry'
import { useStoreMapContext } from '@components/storeMap/provider/StoreMapContext'
import { isTestMode } from '@utils/env'

function ZoomHelper() {
  const { registerZoomControls, isTransformed, setIsTransformed, setZoomAtLimit } = useStoreMapContext()
  const { zoomToElement, zoomIn, zoomOut, resetTransform } = useControls()
  const shelfRegistry = useShelfRegistry<SVGRectElement>()

  // Track transform state and update context
  useTransformEffect(({ state, instance }) => {
    if (isTransformed && state.scale === instance.props.initialScale) setIsTransformed(false)
    if (!isTransformed && state.scale !== instance.props.initialScale) setIsTransformed(true)

    const { minScale, maxScale } = instance.setup

    if (Math.abs(state.scale - minScale) < 0.01) {
      setZoomAtLimit('MIN')
    } else if (Math.abs(state.scale - maxScale) < 0.01) {
      setZoomAtLimit('MAX')
    } else {
      setZoomAtLimit(undefined)
    }
  })

  // Ref to hold the latest zoom functions.
  // These are not stable (new reference on each render), so we use refs
  // to avoid re-registering the zoom handler on every render.
  const zoomToElementRef = useRef(zoomToElement)
  const zoomInRef = useRef(zoomIn)
  const zoomOutRef = useRef(zoomOut)
  const resetTransformRef = useRef(resetTransform)
  useEffect(() => {
    zoomToElementRef.current = zoomToElement
    zoomInRef.current = zoomIn
    zoomOutRef.current = zoomOut
    resetTransformRef.current = resetTransform
  })

  useEffect(() => {
    return registerZoomControls({
      zoomTo: (shelfId: string) => {
        const svgElement = shelfRegistry.get(shelfId)
        if (svgElement === undefined) return

        zoomToElementRef.current(svgElement.id, 1.1, isTestMode ? 1 : 3000)
      },
      zoomIn: () => {
        zoomInRef.current()
      },
      zoomOut: () => {
        zoomOutRef.current()
      },
      resetZoom: () => {
        resetTransformRef.current()
      },
    })
  }, [shelfRegistry, registerZoomControls])

  return null
}

export default ZoomHelper